'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { toast } from 'sonner';
import { ArrowLeft, Fingerprint, KeyRound, Loader2, Lock, Mail, ShieldCheck } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { useAuth } from '@/lib/hooks/use-auth';

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters long'),
  rememberMe: z.boolean(),
});

type LoginSchemaType = z.infer<typeof loginSchema>;

export default function LoginForm() {
  const router = useRouter();
  const {
    login,
    isLoggingIn,
    mfaRequired,
    mfaToken,
    verifyTotp,
    isVerifyingTotp,
    passkeyLogin,
    isPasskeyLoggingIn,
    clearMfa,
  } = useAuth();

  const [totpCode, setTotpCode] = useState('');

  const form = useForm<LoginSchemaType>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
      rememberMe: false,
    },
  });

  const onSubmit = async (values: LoginSchemaType) => {
    try {
      const response = await login(values);
      const data = response.success ? response.data : (response as any);
      if (data?.mfaRequired) {
        toast.info('Two-Factor Authentication Required', {
          description: 'Please enter the 6-digit code from your authenticator app.',
        });
      } else if (response.success || data?.accessToken) {
        toast.success('Access Granted', {
          description: 'Welcome back to Bhulekh-Buddy administration.',
        });
        router.push('/');
      } else {
        toast.error('Authentication Failed', {
          description: response.message || 'Check your credentials and try again.',
        });
      }
    } catch (error: any) {
      toast.error('Authentication Error', {
        description: error.response?.data?.message || 'Unable to connect to the authentication server.',
      });
    }
  };

  const handlePasskeyLogin = async () => {
    const emailValue = form.getValues('email');
    const rememberMe = form.getValues('rememberMe');
    try {
      const response = await passkeyLogin({ email: emailValue, rememberMe });
      const data = response.success ? response.data : (response as any);
      if (data?.mfaRequired) {
        toast.info('Two-Factor Authentication Required', {
          description: 'Please enter the 6-digit code from your authenticator app.',
        });
      } else if (response.success || data?.accessToken) {
        toast.success('Passkey Authenticated', {
          description: 'Access granted via biometric / passkey credential.',
        });
        router.push('/');
      }
    } catch (error: any) {
      toast.error('Passkey Sign In Error', {
        description: error.response?.data?.message || error.message || 'Passkey verification failed.',
      });
    }
  };

  const handleTotpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!totpCode || totpCode.length < 6) {
      toast.error('Invalid Code', { description: 'Please enter a 6-digit TOTP code or backup code.' });
      return;
    }
    if (!mfaToken) {
      toast.error('Session Expired', { description: 'Please log in again.' });
      clearMfa();
      return;
    }

    try {
      const response = await verifyTotp({ mfaToken, code: totpCode, rememberMe: form.getValues('rememberMe') });
      if (response.success || (response as any)?.accessToken) {
        toast.success('2FA Verified', {
          description: 'Access granted. Redirecting to portal...',
        });
        router.push('/');
      } else {
        toast.error('Verification Failed', {
          description: response.message || 'Invalid TOTP code.',
        });
      }
    } catch (error: any) {
      toast.error('2FA Verification Error', {
        description: error.response?.data?.message || 'Invalid verification code or backup code.',
      });
    }
  };

  if (mfaRequired) {
    return (
      <Card className="border-slate-800/60 bg-slate-900/60 backdrop-blur-xl shadow-2xl">
        <CardHeader className="space-y-1 text-center">
          <div className="mx-auto bg-blue-500/10 p-3 rounded-full w-fit mb-2">
            <ShieldCheck className="h-8 w-8 text-blue-400" />
          </div>
          <CardTitle className="text-xl font-bold tracking-tight text-white">
            Two-Factor Verification
          </CardTitle>
          <CardDescription className="text-slate-400">
            Enter the 6-digit verification code from your authenticator app (or a recovery backup code)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleTotpSubmit} className="space-y-4">
            <div>
              <div className="relative">
                <KeyRound className="absolute left-3 top-3 h-4 w-4 text-slate-500" />
                <Input
                  type="text"
                  placeholder="123456 or Backup Code"
                  maxLength={16}
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value)}
                  className="pl-10 text-center tracking-widest font-mono text-lg border-slate-800 bg-slate-950/50 text-white focus-visible:ring-blue-500/50"
                  autoFocus
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={isVerifyingTotp}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white border-none shadow-md shadow-blue-500/10 h-10 font-medium"
            >
              {isVerifyingTotp ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Verifying Code...
                </>
              ) : (
                'Verify & Access Portal'
              )}
            </Button>

            <Button
              type="button"
              variant="ghost"
              onClick={clearMfa}
              className="w-full text-slate-400 hover:text-white hover:bg-slate-800/50"
            >
              <ArrowLeft className="mr-2 h-4 w-4" /> Back to Sign In
            </Button>
          </form>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-slate-800/60 bg-slate-900/60 backdrop-blur-xl shadow-2xl">
      <CardHeader className="space-y-1">
        <CardTitle className="text-xl font-bold tracking-tight text-white">
          Sign In
        </CardTitle>
        <CardDescription className="text-slate-400">
          Enter your administrative credentials to access the portal
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-slate-300">Email Address</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-slate-500" />
                      <Input
                        placeholder="admin@bhulekhbuddy.in"
                        className="pl-10 border-slate-800 bg-slate-950/50 text-white placeholder-slate-600 focus-visible:ring-blue-500/50 focus-visible:border-blue-500"
                        {...field}
                      />
                    </div>
                  </FormControl>
                  <FormMessage className="text-rose-400" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <div className="flex justify-between items-center">
                    <FormLabel className="text-slate-300">Password</FormLabel>
                    <Link
                      href="/forgot-password"
                      className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
                    >
                      Forgot password?
                    </Link>
                  </div>
                  <FormControl>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-slate-500" />
                      <Input
                        type="password"
                        placeholder="••••••••"
                        className="pl-10 border-slate-800 bg-slate-950/50 text-white placeholder-slate-600 focus-visible:ring-blue-500/50 focus-visible:border-blue-500"
                        {...field}
                      />
                    </div>
                  </FormControl>
                  <FormMessage className="text-rose-400" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="rememberMe"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center space-x-2 space-y-0 pt-1">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      className="border-slate-700 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                    />
                  </FormControl>
                  <FormLabel className="text-sm font-normal text-slate-300 cursor-pointer">
                    Remember me on this device (30-day session)
                  </FormLabel>
                </FormItem>
              )}
            />

            <Button
              type="submit"
              disabled={isLoggingIn}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white border-none shadow-md shadow-blue-500/10 h-10 mt-2 font-medium"
            >
              {isLoggingIn ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Verifying...
                </>
              ) : (
                'Log In'
              )}
            </Button>
          </form>
        </Form>

        <div className="relative my-4">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-slate-800" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-slate-900 px-2 text-slate-500 font-medium">Or continue with</span>
          </div>
        </div>

        <Button
          type="button"
          variant="outline"
          onClick={handlePasskeyLogin}
          disabled={isPasskeyLoggingIn}
          className="w-full border-slate-800 bg-slate-950/40 text-slate-200 hover:bg-slate-800/80 hover:text-white"
        >
          {isPasskeyLoggingIn ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Scanning Passkey...
            </>
          ) : (
            <>
              <Fingerprint className="mr-2 h-4 w-4 text-emerald-400" />
              Sign in with Passkey / Biometric
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}
